<template>
	<div class="patient-form">
		<v-form ref="form" v-model="valid" lazy-validation>
			<v-container fluid>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" sm="8">
						<v-text-field
							v-model="patient.name"
							:rules="nameRules"
							:label="$gettext('Name')"
							class="px-2"
							outlined
							@input="setEditMode"
							placeholder=" "
						/>
					</v-col>
					<v-col cols="12" sm="4">
						<v-select
							:items="genders"
							:label="$gettext('Gender')"
							v-model="patient.gender"
							outlined
							class="pr-2"
							:rules="genderRules"
							@input="setEditMode"
							placeholder=" "
						></v-select>
					</v-col>
					<v-col cols="12" sm="6">
						<v-text-field v-model="patient.email" @input="setEditMode" label="Email" class="px-2" outlined placeholder=" " :rules="emailRules" />
					</v-col>
					<v-col cols="12" sm="3">
						<v-menu
							v-model="dateMenu"
							:close-on-content-click="false"
							:nudge-right="40"
							transition="scale-transition"
							offset-y
							min-width="290px"
						>
							<template v-slot:activator="{ on }">
								<v-text-field
									v-model="patient.date_birth"
									:label="$gettext('Birthdate')"
									append-icon="event"
									readonly
									v-on="on"
									outlined
									:rules="birthDateRules"
									placeholder=" "
								></v-text-field>
							</template>
							<v-date-picker color="primary" v-model="patient.date_birth" @input="dateMenu = false; setEditMode()"></v-date-picker>
						</v-menu>
					</v-col>
					<v-col cols="12" sm="3">
						<v-text-field v-model="age" :label="$gettext('Age')" class="px-2" disabled outlined placeholder=" " />
					</v-col>
					<v-col cols="12" sm="4">
						<VuePhoneNumberInput
							v-model="patient.phone"
							valid-color="#009688"
							:style="{'margin-top': '10px', 'padding': '0 8px'}"
							:translations="phoneInputTranslations"
							:error="phoneError"
							@update="onUpdatePhone"
							@phone-number-focused="isPhoneFieldFocused = true"
							@phone-number-blur="isPhoneFieldFocused = false"
							:no-use-browser-locale="!!patient.phone"
						/>
					</v-col>
					<v-col cols="12" sm="4">
						<v-text-field v-model="patient.id_number" @input="setEditMode" :label="$gettext('ID Number')" class="pr-2" outlined placeholder=" " />
					</v-col>
					<v-col cols="12" sm="4">
						<v-text-field v-model="patient.fiscal" @input="setEditMode" :label="$gettext('VAT Number')" class="pr-2" outlined placeholder=" " />
					</v-col>
					<v-col cols="12" sm="6">
						<v-text-field
							v-model="patient.social_security"
							:label="$gettext('Social Security Number')"
							class="px-2"
							outlined
							@input="setEditMode"
							placeholder=" "
						/>
					</v-col>
					<v-col cols="12" sm="6">
						<v-text-field
							v-model="patient.health_system"
							:label="$gettext('Health System Number')"
							class="pr-2"
							outlined
							@input="setEditMode"
							placeholder=" "
						/>
					</v-col>
					<v-col cols="12" sm="12">
						<v-text-field v-model="patient.address" @input="setEditMode" :label="$gettext('Address')" class="px-2" outlined placeholder=" " />
					</v-col>
					<v-col cols="12" sm="6">
						<v-text-field
							v-model="patient.profession"
							:label="$gettext('Occupation')"
							class="px-2"
							outlined
							@input="setEditMode"
							placeholder=" "
						/>
					</v-col>
					<v-col cols="12" sm="6">
						<v-select
							:items="marital"
							:label="$gettext('Marital Status')"
							v-model="patient.civil_status"
							class="pr-2"
							outlined
							@input="setEditMode"
							placeholder=" "
						></v-select>
					</v-col>
					<v-col v-if="shouldShowAppointmentNotifications" cols="12" class="pl-5">
						<p class="mb-3 primary--text">
							<b v-translate>Receive appointment notification via</b>
						</p>
						<v-row v-if="$store.getters.spaceNotifyEmail" no-gutters>
							<v-checkbox
								v-model="patient.receive_email"
								label="Email"
								color="primary"
								class="my-0"
								@change="setEditMode"
							/>
						</v-row>
						<v-row v-if="$store.getters.spaceNotifySMS" no-gutters>
							<v-checkbox
								v-model="patient.receive_sms"
								label="SMS"
								color="primary"
								class="my-0"
								@change="setEditMode"
							/>
						</v-row>
					</v-col>
					<template v-if="age && age < 16">
						<v-col cols="12">
							<v-row no-gutters class="mt-3 mb-3 align-center">
								<span class="text-subtitle-1 font-weight-regular primary--text" v-translate key="my-modules">Legal Representative</span>
							</v-row>
							<v-divider class="mb-3"></v-divider>
						</v-col>
						<v-col cols="12" sm="6">
							<v-text-field
								v-model="patient.representative_name"
								:rules="nameRules"
								:label="$gettext('Name')"
								class="px-2"
								outlined
								@input="setEditMode"
								placeholder=" "
							/>
						</v-col>
						<v-col cols="12" sm="3">
							<v-text-field v-model="patient.representative_id_number" @input="setEditMode" :label="$gettext('ID Number')" class="pr-2" outlined placeholder=" " />
						</v-col>
						<v-col cols="12" sm="3">
							<v-text-field v-model="patient.representative_fiscal" @input="setEditMode" :label="$gettext('VAT Number')" class="pr-2" outlined placeholder=" " />
						</v-col>
					</template>
					<v-col cols="12" sm="6">
						<v-alert v-model="errorPlan" color="red" icon="warning">
							<translate>You reached the maximum number of patients under your plan subscription</translate>
						</v-alert>
						<v-alert v-model="error" class="text-caption" color="red lighten-2" icon="warning">
							<translate>Error</translate>
						</v-alert>
						<v-alert v-model="phoneNumberWarning" color="primary lighten-5" icon="warning" class="text-caption">
							<translate>Only portuguese phone numbers are included in our SMS plan.</translate>
						</v-alert>
					</v-col>
					<v-col cols="12" sm="6">
						<v-btn
							color="primary"
							:loading="saving"
							class="float-right"
							@click="editPatient"
							:disabled="!editMode"
						>
							<translate>Save</translate>
						</v-btn>
					</v-col>
				</v-row>
			</v-container>
		</v-form>
	</div>
</template>

<script>
import axios from "axios";
import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';

export default {
	props: {
		patient: {
			default: () => ({
				name: "",
				gender: "",
				email: "",
				date_birth: "",
				age: "",
				phone: "",
				fiscal: "",
				social_security: "",
				health_system: "",
				address: "",
				profession: "",
				civil_status: "",
				receive_email: false,
				receive_sms: false
			})
		},
		edit: { default: false }
	},
	components: {
		VuePhoneNumberInput
	},
	data: () => ({
		valid: true,
		saving: false,
		dateMenu: false,
		error: false,
		errorPlan: false,
		editMode: false,
		phoneData: {},
		isPhoneFieldFocused: false
	}),
	computed: {
		nameRules() {
			return [v => !!v || this.$gettext("Name is required")];
		},
		genderRules() {
			return [v => !!v || this.$gettext("Gender is required")];
		},
		emailRules() {
			return [
				v => !!v || (!v && !this.patient.receive_email) || this.$gettext("E-mail is required"),
				v => !v || (v && /.+@.+\..+/.test(v)) || this.$gettext("E-mail must be valid")
			]
		},
		birthDateRules() {
			return [v => !!v || this.$gettext("Birthdate is required")];
		},
		genders() {
			return [
				{ text: this.$gettext("Male"), value: "male" },
				{ text: this.$gettext("Female"), value: "female" }
			];
		},
		marital() {
			return [
				{ text: this.$gettext("Single"), value: "single" },
				{ text: this.$gettext("Maried"), value: "married" },
				{ text: this.$gettext("Divorced"), value: "divorced" },
				{ text: this.$gettext("Widowed"), value: "widow" }
			];
		},
		age() {
			if (this.patient.date_birth) {
				const today = new Date();
				const birthdate = new Date(this.patient.date_birth);
				var ageDifMs = today - birthdate;
				var ageDate = new Date(ageDifMs);
				return Math.abs(ageDate.getUTCFullYear() - 1970);
			} else {
				return null;
			}
		},
		phoneInputTranslations() {
			return {
				countrySelectorLabel: this.$gettext('Country code'),
				countrySelectorError: this.$gettext('Choose a country'),
				phoneNumberLabel: this.$gettext('Phone'),
				example: this.$gettext('Example : ')
			}
		},
		phoneError() {
			if (this.patient.receive_sms) {
				return !this.isPhoneFieldFocused && !this.phoneData.isValid;
			}

			return this.patient.phone && !this.isPhoneFieldFocused && !this.phoneData.isValid;
		},
		phoneNumberWarning() {
			return !!this.patient.receive_sms && !!this.phoneData.countryCode && this.phoneData.countryCode !== "PT";
		},
		shouldShowAppointmentNotifications() {
			return this.$store.getters.spaceNotifySMS || this.$store.getters.spaceNotifyEmail
		}
	},
	methods: {
		validate() {
			return this.$refs.form.validate() && !this.phoneError;
		},
		editPatient() {
			if (this.validate()) {
				this.saving = true;
				this.error = false;
				const formData = new FormData();
				if (this.patient.name) {
					formData.append("name", this.patient.name);
				}
				if (this.patient.representative_name) {
					formData.append("representative_name", this.patient.representative_name);
				}
				if (this.patient.gender && this.patient.gender != "None") {
					formData.append("gender", this.patient.gender);
				}
				if (this.patient.email) {
					formData.append("email", this.patient.email);
				}
				if (this.patient.date_birth) {
					formData.append("date_birth", this.patient.date_birth);
				}
				if (this.patient.phone) {
					formData.append("phone", this.phoneData.formattedNumber);
				}
				if (this.patient.id_number) {
					formData.append("id_number", this.patient.id_number);
				}
				if (this.patient.representative_id_number) {
					formData.append("representative_id_number", this.patient.representative_id_number);
				}
				if (this.patient.fiscal) {
					formData.append("fiscal", this.patient.fiscal);
				}
				if (this.patient.representative_fiscal) {
					formData.append("representative_fiscal", this.patient.representative_fiscal);
				}
				if (this.patient.social_security) {
					formData.append("social_security", this.patient.social_security);
				}
				if (this.patient.health_system) {
					formData.append("health_system", this.patient.health_system);
				}
				if (this.patient.address) {
					formData.append("address", this.patient.address);
				}
				if (this.patient.profession) {
					formData.append("profession", this.patient.profession);
				}
				if (this.patient.civil_status && this.patient.civil_status != "None") {
					formData.append("civil_status", this.patient.civil_status);
				}
				formData.append("receive_email", this.patient.receive_email);
				formData.append("receive_sms", this.patient.receive_sms);
				formData.append("personal_submit", true);

				if (this.edit) {
					axios
						.post("/patient/" + this.patient.id, formData)
						.then(response => {
							console.log(response.data);
							if (response.data.result == "ok") {
								this.editMode = false;
							} else {
								this.error = true;
							}
							this.saving = false;
						})
						.catch(error => {
							console.log(error);
							this.error = true;
							this.saving = false;
						});
				} else {
					axios
						.post("/patient/new", formData)
						.then(response => {
							console.log(response.data);
							if (response.status === 200) {
								if (response.data.result === "created") {
									this.saving = false;
									this.$router.push({ name: "patient", params: { pid: response.data.id } });
								} else if (response.data.result === "limited") {
									this.errorPlan = true;
									this.saving = false;
								} else {
									this.error = true;
									this.saving = false;
								}
							}
						})
						.catch(error => {
							console.log(error);
							this.error = true;
							this.saving = false;
						});
				}
			}
		},
		setEditMode() {
			if(this.$store.getters.hasAnyModule) this.editMode = true;
		},
		onUpdatePhone(payload) {
			if (this.patient.phone !== payload.formattedNumber) {
				this.setEditMode();
			}

			console.log(payload, this.phoneData.countryCode);

			this.phoneData = payload;
		}
	}
};
</script>